import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Plus, Loader2, Mountain } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { RangeCardPeaks } from '../components/vision/RangeCardPeaks';
import { slideUpVariants, fadeVariants } from '../components/animations/LampshadeAnimations';
export function VisionPeaksPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [ranges, setRanges] = useState([]);
    const [resetPeriods, setResetPeriods] = useState({});
    const [newRangeName, setNewRangeName] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const hasMountedRef = useRef(false);
    // Fetch all vision data
    useEffect(() => {
        const fetchVisionData = async () => {
            if (!user?.id || hasMountedRef.current)
                return;
            hasMountedRef.current = true;
            setIsLoading(true);
            try {
                // Fetch ranges with all nested data in one query
                const { data: rangesData, error: rangesError } = await supabase
                    .from('ranges')
                    .select(`
            *,
            mountains(
              *,
              hills(
                *,
                terrains(
                  *,
                  lengths(
                    *,
                    steps(*)
                  )
                )
              )
            )
          `)
                    .eq('user_id', user.id)
                    .order('position', { ascending: true, nullsFirst: false })
                    .order('created_at', { ascending: true });
                if (rangesError)
                    throw rangesError;
                const rangeIds = (rangesData || []).map((r) => r.id);
                setRanges((rangesData || []));
                // Fetch reset periods for all ranges
                if (rangeIds.length > 0) {
                    const { data: periodsData } = await supabase
                        .from('reset_periods')
                        .select('*')
                        .in('range_id', rangeIds)
                        .eq('user_id', user.id)
                        .order('created_at', { ascending: false });
                    const periodsMap = {};
                    if (periodsData) {
                        for (const period of periodsData) {
                            if (!periodsMap[period.range_id]) {
                                periodsMap[period.range_id] = period;
                            }
                        }
                    }
                    // Ensure all ranges have an entry (even if null)
                    for (const rangeId of rangeIds) {
                        if (!periodsMap[rangeId]) {
                            periodsMap[rangeId] = null;
                        }
                    }
                    setResetPeriods(periodsMap);
                }
            }
            catch (err) {
                console.error('Error fetching vision data:', err);
                setError(err.message || 'Failed to load vision data');
            }
            finally {
                setIsLoading(false);
            }
        };
        fetchVisionData();
    }, [user?.id]);
    const handleAddRange = async () => {
        if (!newRangeName.trim() || !user?.id)
            return;
        setIsAdding(true);
        try {
            const { data, error } = await supabase
                .from('ranges')
                .insert({
                user_id: user.id,
                name: newRangeName,
            })
                .select(`
          *,
          mountains(
            *,
            hills(
              *,
              terrains(
                *,
                lengths(
                  *,
                  steps(*)
                )
              )
            )
          )
        `)
                .single();
            if (error)
                throw error;
            setRanges((prev) => [...prev, data]);
            setNewRangeName('');
        }
        catch (err) {
            console.error('Error creating range:', err);
            alert('Failed to create range');
        }
        finally {
            setIsAdding(false);
        }
    };
    const handleUpdateRange = (updatedRange) => {
        setRanges((prev) => prev.map((r) => (r.id === updatedRange.id ? updatedRange : r)));
    };
    const handleDeleteRange = (rangeId) => {
        setRanges((prev) => prev.filter((r) => r.id !== rangeId));
    };
    const handleSaveResetPeriod = async (rangeId, period) => {
        try {
            const existingPeriod = resetPeriods[rangeId];
            if (existingPeriod?.id) {
                // Update existing period
                await supabase
                    .from('reset_periods')
                    .update(period)
                    .eq('id', existingPeriod.id)
                    .eq('user_id', user.id);
            }
            else {
                // Create new period
                const { data } = await supabase
                    .from('reset_periods')
                    .insert({
                    range_id: rangeId,
                    user_id: user.id,
                    ...period,
                })
                    .select()
                    .single();
                if (data) {
                    setResetPeriods((prev) => ({
                        ...prev,
                        [rangeId]: data,
                    }));
                    return;
                }
            }
            // Update the local state with the new period
            setResetPeriods((prev) => ({
                ...prev,
                [rangeId]: {
                    ...existingPeriod,
                    ...period,
                },
            }));
        }
        catch (err) {
            console.error('Error saving reset period:', err);
            throw err;
        }
    };
    if (isLoading) {
        return (_jsxs("div", { className: "flex items-center justify-center h-full flex-col gap-4", style: { backgroundColor: 'var(--bg-base)' }, children: [_jsx(motion.div, { animate: { rotate: 360 }, transition: { duration: 2, repeat: Infinity, ease: 'linear' }, children: _jsx(Loader2, { className: "w-8 h-8", style: { color: 'var(--text-secondary)' } }) }), _jsx("p", { style: { color: 'var(--text-secondary)' }, children: "Loading your vision peaks..." })] }));
    }
    if (error) {
        return (_jsxs("div", { className: "flex flex-col items-center justify-center h-full gap-4", style: { backgroundColor: 'var(--bg-base)' }, children: [_jsx(Mountain, { className: "w-12 h-12", style: { color: 'var(--text-secondary)' } }), _jsxs("p", { style: { color: 'var(--text-primary)' }, children: ["Error: ", error] }), _jsx(Button, { onClick: () => navigate('/app/app-switcher'), variant: "outline", children: "Back to Apps" })] }));
    }
    return (_jsxs(motion.div, { className: "flex flex-col h-full", style: { backgroundColor: 'var(--bg-base)' }, initial: "hidden", animate: "visible", exit: "hidden", variants: fadeVariants, children: [_jsxs(motion.div, { className: "px-4 md:px-8 py-4 md:py-6 border-b sticky top-0 z-10", style: { borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-base)' }, variants: slideUpVariants, children: [_jsxs("div", { className: "flex items-center justify-between mb-4", children: [_jsxs(Button, { variant: "ghost", onClick: () => navigate('/app/app-switcher'), style: { backgroundColor: 'var(--bg-elevated)', color: 'var(--text-secondary)' }, className: "h-8 px-2 md:h-10 md:px-4", children: [_jsx(ArrowLeft, { className: "w-4 h-4 mr-1 md:mr-2" }), _jsx("span", { className: "hidden sm:inline", children: "Back" })] }), _jsx("div", { className: "flex items-center gap-2", children: _jsx(Mountain, { className: "w-6 h-6 md:w-8 md:h-8", style: { color: '#8B5CF6' } }) })] }), _jsxs("div", { children: [_jsx("h1", { className: "text-2xl md:text-3xl font-bold", style: { color: 'var(--text-primary)' }, children: "Vision Peaks" }), _jsx("p", { className: "text-xs md:text-sm mt-1", style: { color: 'var(--text-secondary)' }, children: "Climb your mountains, reach your goals" })] })] }), _jsxs("div", { className: "flex-1 overflow-y-auto px-4 md:px-8 py-6 space-y-4 md:space-y-6", children: [ranges.length === 0 ? (_jsxs(motion.div, { className: "flex flex-col items-center justify-center py-12", variants: slideUpVariants, children: [_jsx(Mountain, { className: "w-16 h-16 mb-4", style: { color: 'var(--text-secondary)', opacity: 0.5 } }), _jsx("h2", { className: "text-xl font-semibold mb-2", style: { color: 'var(--text-primary)' }, children: "No peaks yet" }), _jsx("p", { style: { color: 'var(--text-secondary)' }, className: "text-center max-w-xs mb-6", children: "Create your first range to start building your vision" })] })) : (_jsx(motion.div, { className: "space-y-4", variants: { visible: { transition: { staggerChildren: 0.1 } } }, initial: "hidden", animate: "visible", children: ranges.map((range, idx) => (_jsx(motion.div, { variants: slideUpVariants, custom: idx, children: _jsx(RangeCardPeaks, { range: range, userId: user.id, resetPeriod: resetPeriods[range.id] || null, onUpdate: handleUpdateRange, onDelete: handleDeleteRange, onSaveResetPeriod: handleSaveResetPeriod }) }, range.id))) })), _jsx(motion.div, { variants: slideUpVariants, className: "mt-8 md:mt-12", children: _jsxs("div", { className: "rounded-2xl p-6 backdrop-blur-xl border shadow-lg", style: {
                                backgroundColor: 'rgba(15, 23, 42, 0.7)',
                                borderColor: 'rgba(14, 165, 233, 0.2)',
                            }, children: [_jsxs("h3", { className: "text-lg font-semibold mb-4 flex items-center gap-2", style: { color: 'var(--text-primary)' }, children: [_jsx(Plus, { className: "w-5 h-5", style: { color: '#8B5CF6' } }), "Create a New Range"] }), _jsxs("form", { onSubmit: async (e) => {
                                        e.preventDefault();
                                        await handleAddRange();
                                    }, className: "flex gap-2", children: [_jsx(Input, { value: newRangeName, onChange: (e) => setNewRangeName(e.target.value), placeholder: "e.g., 2025 Personal Growth", className: "flex-1", style: { backgroundColor: 'var(--bg-base)', color: 'var(--text-primary)' } }), _jsx(Button, { type: "submit", disabled: isAdding || !newRangeName.trim(), style: { backgroundColor: '#8B5CF6', color: 'white' }, className: "px-4", children: isAdding ? _jsx(Loader2, { className: "w-4 h-4 animate-spin" }) : _jsx(Plus, { className: "w-4 h-4" }) })] }), _jsx("p", { className: "text-xs mt-3", style: { color: 'var(--text-secondary)' }, children: "\uD83D\uDCA1 A range is like a year or theme. Add mountains (big goals) to each range." })] }) }), _jsx("div", { className: "h-8" })] })] }));
}
